import React, { useState } from 'react';
import cx from 'classnames';
import { CaseDetails } from '../../../CaseDetailsPage.types';
import SubPageTemplate from '../../../partials/SubPageTemplate/SubPageTemplate';
import { createState, State, VulnerabilityItem } from 'modules/cm/Vulnerabilities/store/store';
import { Box } from 'lmc-ui-library';
import css from './Vulnerabilities.module.scss';
import NoItems from './NoItems';
import VulnerabilityListItem from './VulnerabilityListItem';
import { CaseId } from 'modules/cm/api/cases/types';
import { useEffect } from 'react';
import { Vulnerability, VulnerabilityField, VulnerabilityId } from 'modules/cm/Vulnerabilities/Vulnerability';
import CreatedVulnerabilityListItem from './CreatedVulnerabilityListItem';
import { Button } from 'modules/af_cm_ui_kit';
import { Form, FormVulnerbaility } from './Vulnerability';
import { useDispatch } from 'react-redux';
import { saveCase, setErrorModal } from 'modules/cm/store/cases/actions';
import useSaveButtonClicked from '../../../hooks/useSaveButtonClicked';

interface Props {
  caseId: string;
  applicantIndex: 0 | 1;
  applicantId: number;
  applicantsVulnerabilities: State;
  resources: CaseDetails['resources'];
  getVulnerabilities: (caseId: CaseId, applicantId: number, applicantIndex: number) => void;
  addNewVulnerability: (
    caseId: CaseId,
    applicantId: number,
    applicantIndex: number,
    vulnerability: FormVulnerbaility,
  ) => void;
  deleteVulnerability: (
    caseId: CaseId,
    applicantId: number,
    applicantIndex: number,
    vulnerability: Vulnerability,
  ) => void;
  updateVulnerability: (
    caseId: CaseId,
    applicantId: number,
    applicantIndex: number,
    vulnerabilityId: VulnerabilityId,
    vulnerability: FormVulnerbaility,
  ) => void;
}

const removeItemFromList = (index: number, list: Array<any>) => {
  const clonedList = [...list];
  clonedList.splice(index, 1);
  return clonedList;
};

const Vulnerabilities = ({
  caseId,
  applicantId,
  applicantIndex,
  applicantsVulnerabilities,
  getVulnerabilities,
  addNewVulnerability,
  deleteVulnerability,
  updateVulnerability,
  resources,
}: Props) => {
  const { vulnerabilityItems } = applicantsVulnerabilities[applicantIndex] || createState()[applicantIndex];
  const [newVulnerabilities, setNewVulnerabilities] = useState<(Form & { id: number })[]>([]);
  const dispatch = useDispatch();
  const { addListener, removeListener } = useSaveButtonClicked();
  useEffect(() => {
    const listener = addListener(() => {
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getVulnerabilities(caseId, applicantId, applicantIndex);
  }, [applicantId, applicantIndex, caseId, getVulnerabilities]);

  const handleAddNewVulnerabilityClick = () => {
    setNewVulnerabilities((state) => [
      ...state,
      {
        category: null,
        dateOfConsent: null,
        id: new Date().getTime(),
        [VulnerabilityField.CommentsAndAdditionalInformation]: null,
        [VulnerabilityField.InformationProvidedBy]: null,
        [VulnerabilityField.IsAppropriateForExplicitConsent]: null,
        [VulnerabilityField.IsExplicitConsentFromCustomer]: null,
      },
    ]);
  };

  const handleDeleteItem = (vulnerability: VulnerabilityItem) => {
    try {
      deleteVulnerability(caseId, applicantId, applicantIndex, vulnerability.vulnerability);
    } catch (err) {
      dispatch(setErrorModal(err));
    }
  };

  const handleCancelCreating = (index: number) => {
    setNewVulnerabilities((state) => removeItemFromList(index, state));
  };

  const handleCreateVulnerability = async (index: number, createdVulnerability: FormVulnerbaility) => {
    try {
      await addNewVulnerability(caseId, applicantId, applicantIndex, createdVulnerability);
      setNewVulnerabilities((state) => removeItemFromList(index, state));
    } catch (err) {
      dispatch(setErrorModal(err));
    }
  };

  const handleEditVulnerability = async (vulnerabilityId: VulnerabilityId, vulnerability: FormVulnerbaility) => {
    try {
      await updateVulnerability(caseId, applicantId, applicantIndex, vulnerabilityId, vulnerability);

      return Promise.resolve();
    } catch (err) {
      dispatch(setErrorModal(err));
      return Promise.reject();
    }
  };

  return (
    <SubPageTemplate title="Vulnerability">
      <Box className={css.wrapper}>
        <Box.Content>
          <div className={css.accordion}>
            {vulnerabilityItems.map((item) => (
              <div
                className={cx(css.stack, css.vulnerabilityItems, css.vulnerabilityItem, {
                  [css.inProgress]: item.isLoading,
                })}
                key={`existing-vulnerabilty-${item.vulnerability.id}`}>
                <VulnerabilityListItem
                  item={item}
                  categoryResources={resources.vulnerabilityCategories}
                  informationResources={resources.informationProvidedBy}
                  onDelete={handleDeleteItem.bind(null, item)}
                  onSave={handleEditVulnerability}
                />
              </div>
            ))}

            {newVulnerabilities.map((item, i) => (
              <div className={cx(css.stack, css.vulnerabilityItems)} key={`new-vulnerbaility-${item.id}`}>
                <CreatedVulnerabilityListItem
                  item={item}
                  categoryResources={resources.vulnerabilityCategories}
                  informationResources={resources.informationProvidedBy}
                  onCancel={handleCancelCreating.bind(null, i)}
                  onCreate={handleCreateVulnerability.bind(null, i)}
                />
              </div>
            ))}

            {vulnerabilityItems.length === 0 && newVulnerabilities.length === 0 && (
              <div className={cx(css.stack, css.noItems)}>
                <NoItems />
              </div>
            )}
          </div>

          <Button mode="primary" onClick={handleAddNewVulnerabilityClick}>
            Add new
          </Button>
        </Box.Content>
      </Box>
    </SubPageTemplate>
  );
};

export default Vulnerabilities;
